import React, { useEffect, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button, Input } from '../../ui'
import { FormField } from '../../ui/Form'
import {
  isValidManualPackage,
  type ManualPackageDimensions
} from '../../../utils/orderQuoteFromOrder'

interface DimensionFields {
  largo: string
  ancho: string
  altura: string
  peso: string
}

interface OrderQuoteDimensionsModalProps {
  isOpen: boolean
  initial: DimensionFields
  onClose: () => void
  onConfirm: (dims: ManualPackageDimensions) => void
}

const FIELDS: Array<{ key: keyof DimensionFields; label: string }> = [
  { key: 'largo', label: 'Largo (CM)' },
  { key: 'ancho', label: 'Ancho (CM)' },
  { key: 'altura', label: 'Alto (CM)' },
  { key: 'peso', label: 'Peso (KG)' }
]

function toPackage(values: DimensionFields): ManualPackageDimensions {
  return {
    largo: Number(values.largo),
    ancho: Number(values.ancho),
    altura: Number(values.altura),
    peso: Number(values.peso)
  }
}

export const OrderQuoteDimensionsModal: React.FC<OrderQuoteDimensionsModalProps> = ({
  isOpen,
  initial,
  onClose,
  onConfirm
}) => {
  const [values, setValues] = useState<DimensionFields>(initial)
  const parsed = toPackage(values)
  const canContinue = isValidManualPackage(parsed)

  useEffect(() => {
    if (isOpen) setValues(initial)
    // Solo reinicia al abrir; si initial cambia mientras se escribe, no se pisan los valores
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex h-full w-full items-center justify-center overflow-y-auto bg-gray-600 bg-opacity-50">
      <div
        className="relative m-4 w-full max-w-lg rounded-lg bg-white p-8 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="order-quote-dimensions-title"
      >
        <div className="mb-6 flex items-center justify-between">
          <h3 id="order-quote-dimensions-title" className="text-lg font-semibold text-gray-900">
            Dimensiones del pedido
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 transition-colors hover:text-gray-700"
            aria-label="Cerrar"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <p className="mb-4 text-sm text-gray-600">
          No se pudieron calcular las medidas desde los productos. Ingresa el largo, ancho, alto y peso totales para continuar con la cotización.
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {FIELDS.map(field => (
            <FormField key={field.key} label={field.label} required>
              <Input
                type="number"
                min="0"
                step="0.1"
                value={values[field.key]}
                mode="edit"
                aria-label={field.label}
                onChange={(event) => {
                  const next = event.target.value
                  setValues(prev => ({ ...prev, [field.key]: next }))
                }}
              />
            </FormField>
          ))}
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="danger"
            disabled={!canContinue}
            onClick={() => onConfirm(parsed)}
          >
            Continuar
          </Button>
        </div>
      </div>
    </div>
  )
}
